import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import api from '../../services/api.ts';
import { formatCurrency } from '../../utils/currency.ts';
import PageHeader from '../../components/ui/PageHeader.tsx';
import StatusChip from '../../components/ui/StatusChip.tsx';
import { useAuth } from '../../contexts/AuthContext.tsx';

const ConciliacaoOfx: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [status, setStatus] = useState('all');
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');

  const { data, isLoading } = useQuery(
    ['ofx-list', status],
    async () => {
      const params: any = {};
      if (status !== 'all') params.status = status;
      const res = await api.get('/financeiro/conciliacao-ofx', { params });
      return res.data as { items: any[]; summary: any; lastImport: any };
    }
  );

  const importMutation = useMutation(
    async (payload: { content: string; filename: string }) => {
      const res = await api.post('/financeiro/conciliacao-ofx/importar', payload);
      return res.data;
    },
    {
      onSuccess: (d: any) => {
        setMsg(`Importados ${d.created} · ignorados (FITID) ${d.skipped}`);
        setError('');
        qc.invalidateQueries('ofx-list');
      },
      onError: (err: any) => setError(err?.response?.data?.error || 'Erro ao importar OFX'),
    }
  );

  const onPickFile = async (file: File | null) => {
    if (!file) return;
    const content = await file.text();
    importMutation.mutate({ content, filename: file.name });
  };

  const canWrite = hasPermission('debts.create') || hasPermission('debts.update');
  const items = data?.items || [];
  const summary = data?.summary;
  const isEmpty = !isLoading && items.length === 0 && !data?.lastImport;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500" />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        breadcrumb="Financeiro / Conciliação OFX"
        title="Conciliação OFX"
        subtitle="Importe extrato bancário e concilie com o fluxo de caixa"
        actions={
          canWrite ? (
            <>
              <input
                ref={fileRef}
                type="file"
                accept=".ofx,.OFX,application/x-ofx,text/plain"
                style={{ display: 'none' }}
                onChange={e => onPickFile(e.target.files?.[0] || null)}
              />
              <button
                type="button"
                className="btn btn-primary"
                disabled={importMutation.isLoading}
                onClick={() => fileRef.current?.click()}
              >
                <ArrowUpTrayIcon style={{ width: 18, height: 18 }} />
                {importMutation.isLoading ? 'Importando…' : 'Importar OFX'}
              </button>
            </>
          ) : undefined
        }
      />

      {error && <div className="field-error" style={{ marginBottom: 12 }}>{error}</div>}
      {msg && <div className="hint-banner" style={{ marginBottom: 12 }}>{msg}</div>}

      {isEmpty ? (
        <div className="card" style={{ padding: 48, textAlign: 'center' }}>
          <div className="page-title" style={{ fontSize: 20, marginBottom: 8 }}>
            Nenhum extrato importado
          </div>
          <p className="muted" style={{ marginBottom: 20 }}>
            Importe um arquivo OFX da sua conta corrente para conciliar com os lançamentos do fluxo.
          </p>
          {canWrite && (
            <button type="button" className="btn btn-primary" onClick={() => fileRef.current?.click()}>
              Importar OFX
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="toolbar">
            <div className="filters">
              <label className="filter-field">
                <span className="filter-label">Status</span>
                <select className="select" value={status} onChange={e => setStatus(e.target.value)}>
                  <option value="all">Todos</option>
                  <option value="nao_conciliada">Não conciliada</option>
                  <option value="sugerida">Sugerida</option>
                  <option value="conciliada">Conciliada</option>
                </select>
              </label>
            </div>
          </div>

          <div className="kpi-row" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
            <div className="kpi">
              <div className="kpi-label">Linhas no extrato</div>
              <div className="kpi-value">{summary?.total || 0}</div>
              <div className="kpi-hint">{data?.lastImport?.filename || '—'}</div>
            </div>
            <div className="kpi">
              <div className="kpi-label">Não conciliadas</div>
              <div className="kpi-value">{summary?.naoConciliada || 0}</div>
            </div>
            <div className="kpi">
              <div className="kpi-label">Sugeridas</div>
              <div className="kpi-value" style={{ color: 'var(--amber-700)' }}>
                {summary?.sugerida || 0}
              </div>
            </div>
            <div className="kpi">
              <div className="kpi-label">Conciliadas</div>
              <div className="kpi-value positive">{summary?.conciliada || 0}</div>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <div className="card-title">Extrato importado</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <StatusChip label="Não conciliada" tone="slate" />
                <StatusChip label="Sugerida" tone="amber" />
                <StatusChip label="Conciliada" tone="green" />
              </div>
            </div>
            <div className="table-wrap">
              <table className="data-table compact-mobile">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Descrição (banco)</th>
                    <th style={{ textAlign: 'right' }}>Valor</th>
                    <th>Status</th>
                    <th className="hide-m">Lançamento</th>
                    <th style={{ textAlign: 'right' }}>Ação</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item: any) => (
                    <tr key={item.id}>
                      <td className="mono">
                        {new Date(item.postedAt + 'T00:00:00').toLocaleDateString('pt-BR')}
                      </td>
                      <td>
                        <div>{item.description}</div>
                        <div className="muted mono" style={{ fontSize: 11 }}>
                          FITID {item.fitid}
                        </div>
                      </td>
                      <td
                        className={`amount ${item.type === 'in' ? 'in' : 'out'}`}
                        style={{ textAlign: 'right' }}
                      >
                        {item.type === 'in' ? '+ ' : '− '}
                        {formatCurrency(item.amount)}
                      </td>
                      <td>
                        <StatusChip label={item.statusLabel} tone={item.statusTone} />
                      </td>
                      <td className="hide-m">
                        {item.lcCode ? (
                          <button
                            type="button"
                            className="link-btn"
                            onClick={() => navigate('/financeiro/fluxo-de-caixa')}
                          >
                            {item.lcCode}
                          </button>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {item.status === 'conciliada' ? (
                          <button
                            type="button"
                            className="link-btn"
                            onClick={() =>
                              navigate(`/financeiro/conciliacao-ofx/${item.id}/desconciliar`)
                            }
                          >
                            Desconciliar
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="link-btn"
                            onClick={() =>
                              navigate(`/financeiro/conciliacao-ofx/${item.id}/conciliar`)
                            }
                          >
                            Conciliar
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ConciliacaoOfx;
